<template>
  <div class="flex gap-0 bg-slate-100 max-md:flex-wrap">
    <div class="flex flex-col flex-1 max-md:max-w-full">
      <div class="flex flex-col items-start self-center pt-7 pr-20 pb-12 pl-6 mt-8 w-full bg-white rounded-3xl max-w-[1285px] max-md:px-5 max-md:max-w-full">
        <!-- Header -->
        <div class="flex gap-4 text-2xl font-medium whitespace-nowrap text-neutral-800 mb-6">
          <button
            @click="handleBackClick"
            class="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Back
          </button>
          <img
            loading="lazy"
            src="@/assets/img/brand.svg"
            class="aspect-[0.95] w-[21px]"
          />
          <div class="grow text-xs sm:text-sm md:text-[22px]">
            Store Locator Configuration
          </div>
        </div>

        <!-- Preview Section -->
        <div class="w-full mb-8">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Live Preview</h3>
          <div class="border-2 border-gray-200 rounded-lg overflow-auto" style="height: 600px;">
            <StoreLocator 
              :embedUid="currentUID"
              :embedWid="WID"
              :storeLocatorStyles="config"
              :disableNavigation="true"
              :disableFavicon="true"
              :disablePageTitle="true"
            />
          </div>
        </div>

        <!-- Configuration Section -->
        <div class="w-full">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Configuration Options</h3>
          
          <!-- Tab Navigation -->
          <div class="flex border-b border-gray-200 mb-6">
            <button
              v-for="tab in configurationTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Tab Content -->
          <div class="min-h-[600px]">
            <!-- Main Tab -->
            <div v-if="activeTab === 'main'" class="space-y-8">
              <!-- Color Scheme Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Color Scheme
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Primary Color -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Primary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.primaryColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.primaryColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Main brand color used throughout the interface</p>
                  </div>

                  <!-- Secondary Color -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Secondary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.secondaryColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.secondaryColor"
                        placeholder="#6B7280"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Supporting color for secondary elements</p>
                  </div>
                </div>
              </div>

              <!-- Header Tabs Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                  </svg>
                  Header Navigation Tabs
                </h4>
                <div class="space-y-4">
                  <p class="text-sm text-gray-600 mb-4">Configure the navigation tabs that appear in the header of your store locator.</p>
                  <div class="space-y-3">
                    <div v-for="(tab, index) in config.headerTabs" :key="index" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div class="flex-1">
                        <input
                          type="text"
                          v-model="tab.label"
                          placeholder="Tab Label (e.g., Overview)"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div class="flex-1">
                        <input
                          type="text"
                          v-model="tab.value"
                          placeholder="tab_value (e.g., overview)"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <button
                        @click="removeTab(index)"
                        class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <button
                    @click="addTab"
                    class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
                  >
                    <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add Tab
                  </button>
                </div>
              </div>
            </div>

            <!-- Header Tab -->
            <div v-if="activeTab === 'header'" class="space-y-8">
              <!-- Header Background Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Header Background & Layout
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Header Background</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.headerBg"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.headerBg"
                        placeholder="#f8fafc"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Background color for the header area</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Tab Button Style</label>
                    <select
                      v-model="selectedTabStyle"
                      @change="applyTabStyle"
                      class="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-gray-400 transition-colors font-medium"
                    >
                      <option value="default">Default Blue</option>
                      <option value="modern">Modern Gray</option>
                      <option value="elegant">Elegant Purple</option>
                      <option value="warm">Warm Orange</option>
                      <option value="nature">Nature Green</option>
                      <option value="minimal">Minimal White</option>
                      <option value="dark">Dark Theme</option>
                      <option value="primary-focus">Primary Focus</option>
                      <option value="secondary-focus">Secondary Focus</option>
                      <option value="primary-gradient">Primary Gradient</option>
                      <option value="secondary-gradient">Secondary Gradient</option>
                      <option value="primary-outline">Primary Outline</option>
                      <option value="secondary-outline">Secondary Outline</option>
                      <option value="primary-soft">Primary Soft</option>
                      <option value="secondary-soft">Secondary Soft</option>
                      <option value="primary-bold">Primary Bold</option>
                      <option value="secondary-bold">Secondary Bold</option>
                      <option value="custom">Custom (Manual)</option>
                    </select>
                    <p class="text-xs text-gray-500">Predefined tab button styling presets</p>
                  </div>
                </div>
              </div>

              <!-- Brand Identity Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Brand Identity
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Logo Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="logoSizeSlider"
                        min="1"
                        max="12"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div class="flex flex-col items-center gap-1">
                        <input
                          type="text"
                          v-model="config.header_styles.logoSizeMobile"
                          placeholder="2.5rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-sm"
                        />
                        <span class="text-xs text-gray-500">Mobile</span>
                      </div>
                      <div class="flex flex-col items-center gap-1">
                        <input
                          type="text"
                          v-model="config.header_styles.logoSizeDesktop"
                          placeholder="5rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-sm"
                        />
                        <span class="text-xs text-gray-500">Desktop</span>
                      </div>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ logoSizeSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Brand Name Text Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="brandNameSizeSlider"
                        min="0.75"
                        max="3"
                        step="0.05"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.brandNameSize"
                        placeholder="1.375rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ brandNameSizeSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Brand Name Text Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.brandNameColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.brandNameColor"
                        placeholder="#000000"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Color for the brand name text</p>
                  </div>
                </div>
              </div>

              <!-- Search Bar Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                  Search Bar Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.header_styles.searchBorderColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Border color for the search input field</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Radius</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="searchBorderRadiusSlider"
                        min="0"
                        max="3"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderRadius"
                        placeholder="1.5rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current radius: {{ searchBorderRadiusSlider }}rem</div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Search Border Size</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="searchBorderSizeSlider"
                        min="0"
                        max="8"
                        step="0.5"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.header_styles.searchBorderSize"
                        placeholder="2px"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500 mt-1">Current size: {{ searchBorderSizeSlider }}px</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sidepanel Tab -->
            <div v-if="activeTab === 'sidepanel'" class="space-y-8">
              <!-- Global Controls Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  Global Controls
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <!-- Border Radius Control -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Border Radius (All States)</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="sidepanelBorderRadiusSlider"
                        min="0"
                        max="2"
                        step="0.1"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedRadius"
                        placeholder="1.25rem"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500">Current: {{ sidepanelBorderRadiusSlider }}rem</div>
                  </div>

                  <!-- Border Size Control -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Border Size (All States)</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="range"
                        v-model="sidepanelBorderSizeSlider"
                        min="0"
                        max="8"
                        step="0.5"
                        class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedBorderSize"
                        placeholder="3px"
                        class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                      />
                    </div>
                    <div class="text-xs text-gray-500">Current: {{ sidepanelBorderSizeSlider }}px</div>
                  </div>

                  <!-- Style Preset Selector -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Style Preset</label>
                    <select
                      v-model="selectedSidepanelStyle"
                      @change="applySidepanelStyle"
                      class="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-gray-400 transition-colors font-medium"
                    >
                      <option value="primary-soft">Primary Soft</option>
                      <option value="primary-bold">Primary Bold</option>
                      <option value="primary-outline">Primary Outline</option>
                      <option value="secondary-soft">Secondary Soft</option>
                      <option value="secondary-bold">Secondary Bold</option>
                      <option value="secondary-outline">Secondary Outline</option>
                      <option value="minimal">Minimal</option>
                      <option value="elegant">Elegant</option>
                      <option value="custom">Custom (Manual)</option>
                    </select>
                    <p class="text-xs text-gray-500">Predefined styling presets</p>
                  </div>
                </div>
              </div>

              <!-- State-Specific Styling -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  State-Specific Styling
                </h4>
                
                <!-- Selected State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    Selected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedBg"
                          placeholder="#e6f7ff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedText"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.selectedBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.selectedBorder"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.selectedBorderSize"
                        placeholder="3px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Hover State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    Hover State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverBg"
                          placeholder="#f0f7ff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverText"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.hoverBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.hoverBorder"
                          placeholder="#1773EA"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.hoverBorderSize"
                        placeholder="2px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Unselected State -->
                <div>
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                    Unselected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Background Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedBg"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedText"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedText"
                          placeholder="#222"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.sidepanel_styles.unselectedBorder"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.sidepanel_styles.unselectedBorder"
                          placeholder="#e0e7ef"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Border Size</label>
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.unselectedBorderSize"
                        placeholder="1px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Icons Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Icons & Visual Elements
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Show Icons</label>
                    <div class="flex items-center gap-3">
                      <label class="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          v-model="showIconsToggle"
                          class="sr-only"
                        />
                        <div class="relative">
                          <div class="block bg-gray-600 w-14 h-8 rounded-full"></div>
                          <div class="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition"></div>
                        </div>
                        <span class="ml-3 text-sm text-gray-700">{{ showIconsToggle ? 'Enabled' : 'Disabled' }}</span>
                      </label>
                    </div>
                    <p class="text-xs text-gray-500">Toggle visibility of location icons</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Icon Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.sidepanel_styles.iconColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.iconColor"
                        placeholder="#1773EA"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Chevron Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.sidepanel_styles.chevronColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.sidepanel_styles.chevronColor"
                        placeholder="rgba(23, 115, 234, 0.7)"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Panel Tab -->
            <div v-if="activeTab === 'details'" class="space-y-8">
              <!-- Panel Layout Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Panel Layout & Appearance
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Background</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.bg"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.bg"
                        placeholder="#fff"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Background color for the details panel</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Text Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.textColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.textColor"
                        placeholder="#222"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Text color for panel content</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.borderColor"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.borderColor"
                        placeholder="#1976d2"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Border color for the panel outline</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Size</label>
                    <input
                      type="text"
                      v-model="config.details_styles.common.borderSize"
                      placeholder="2px"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p class="text-xs text-gray-500">Thickness of the panel border</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Panel Border Radius</label>
                    <input
                      type="text"
                      v-model="config.details_styles.common.borderRadius"
                      placeholder="1.25rem"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p class="text-xs text-gray-500">Corner roundness of the panel</p>
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Primary Color</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="config.details_styles.common.primary"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="config.details_styles.common.primary"
                        placeholder="#1976d2"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">Primary accent color for highlights</p>
                  </div>
                </div>
              </div>

              <!-- Button Styling Section -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.122 2.122"></path>
                  </svg>
                  Button Styling & Interactions
                </h4>
                
                <!-- Global Button Controls -->
                <div class="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 class="text-md font-medium text-blue-800 mb-4 flex items-center">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    Global Button Controls
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Radius (All States)</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="range"
                          v-model="buttonBorderRadiusSlider"
                          min="0"
                          max="2"
                          step="0.1"
                          class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderRadius"
                          placeholder="0.75rem"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        />
                      </div>
                      <div class="text-xs text-gray-500">Current: {{ buttonBorderRadiusSlider }}rem</div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Size (All States)</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="range"
                          v-model="buttonBorderSizeSlider"
                          min="0"
                          max="8"
                          step="0.5"
                          class="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderSize"
                          placeholder="2px"
                          class="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                        />
                      </div>
                      <div class="text-xs text-gray-500">Current: {{ buttonBorderSizeSlider }}px</div>
                    </div>
                  </div>
                </div>

                <!-- Default State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    Default State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.textColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.textColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.bg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.bg"
                          placeholder="#1976d2"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.borderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.borderColor"
                          placeholder="#1976d2"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Button Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.borderSize"
                        placeholder="2px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Hover State -->
                <div class="mb-8">
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    Hover State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverTextColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverTextColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverBg"
                          placeholder="#125bb5"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.hoverBorderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.hoverBorderColor"
                          placeholder="#125bb5"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Hover Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.hoverBorderSize"
                        placeholder="2.5px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <!-- Selected State -->
                <div>
                  <h5 class="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <div class="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    Selected State
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Text Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedTextColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedTextColor"
                          placeholder="#fff"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Background</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedBg"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedBg"
                          placeholder="#388e3c"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Border Color</label>
                      <div class="flex items-center gap-3">
                        <input
                          type="color"
                          v-model="config.details_styles.button.selectedBorderColor"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          type="text"
                          v-model="config.details_styles.button.selectedBorderColor"
                          placeholder="#388e3c"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">Selected Border Size</label>
                      <input
                        type="text"
                        v-model="config.details_styles.button.selectedBorderSize"
                        placeholder="3px"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer Tab -->
            <div v-if="activeTab === 'footer'" class="space-y-6">
              <div class="text-center py-12">
                <h4 class="text-lg font-medium text-gray-800 mb-4">Footer Configuration</h4>
                <p class="text-gray-600">Footer configuration options will be added here in a future update.</p>
                <div class="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p class="text-sm text-gray-500">Coming soon...</p>
              </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons - Visible in every tab -->
          <div class="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              @click="handleSaveClick"
              class="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
            >
              Save Configuration
            </button>
            <button
              @click="resetToDefaults"
              class="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
            >
              Reset to Defaults
            </button>
            <button
              @click="revertAllChanges"
              class="px-6 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium"
            >
              Revert All Changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialogs -->
    <!-- Save Confirmation Dialog -->
    <div v-if="showSaveDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to save all the configuration changes? This will update your store locator settings.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showSaveDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmSave"
            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>

    <!-- Back Confirmation Dialog -->
    <div v-if="showBackDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Unsaved Changes</h3>
        <p class="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to go back? All unsaved changes will be lost.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showBackDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Stay Here
          </button>
          <button
            @click="confirmBack"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StoreLocator from '@/components/locations/StoreLocator.vue';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/utils/firebase.utils';

export default {
  name: 'StoreLocatorConfigurations',
  components: {
    StoreLocator
  },
  data() {
    return {
      showSaveDialog: false,
      showBackDialog: false,
      originalConfig: null,
      selectedTabStyle: 'default',
      selectedSidepanelStyle: 'primary-soft',
      showIconsToggle: true,
      activeTab: 'main',
      configurationTabs: [
        { id: 'main', label: 'Main Settings' },
        { id: 'header', label: 'Header' },
        { id: 'sidepanel', label: 'Sidepanel' },
        { id: 'details', label: 'Details Panel' },
        { id: 'footer', label: 'Footer' }
      ],
      config: {
        primaryColor: '#1773EA',
        secondaryColor: '#6B7280',
        header_styles: {
          headerBg: '#f8fafc',
          tabTextColor: '#222',
          tabHoverTextColor: '#1773EA',
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: '#1773EA',
          searchBorderColor: '#1773EA',
          searchBorderRadius: '1.5rem',
          searchBorderSize: '2px',
          logoSizeMobile: '2.5rem',
          logoSizeDesktop: '5rem',
          brandNameSize: '1.375rem',
          brandNameColor: '#000000',
        },
        sidepanel_styles: {
          selectedBg: '#e6f7ff',
          selectedText: '#1773EA',
          selectedBorder: '#1773EA',
          selectedBorderSize: '3px',
          selectedRadius: '1.25rem',
          hoverBg: '#f0f7ff',
          hoverText: '#1773EA',
          hoverBorder: '#1773EA',
          hoverBorderSize: '2px',
          hoverRadius: '1.25rem',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px',
          unselectedRadius: '0.75rem',
          iconColor: '#1773EA',
          chevronColor: 'rgba(23, 115, 234, 0.7)',
          showIcons: 'all',
        },
        headerTabs: [
          { label: 'Overview', value: 'overview' },
          { label: 'Map', value: 'map' },
          { label: 'Reviews', value: 'reviews' }
        ],
        details_styles: {
          common: {
            bg: '#fff',
            textColor: '#222',
            borderColor: '#1976d2',
            borderSize: '2px',
            borderRadius: '1.25rem',
            primary: '#1976d2',
            secondary: '#424242',
            accent: '#82B1FF',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FFC107',
            error: '#FF5252',
          },
          button: {
            textColor: '#fff',
            bg: '#1976d2',
            borderColor: '#1976d2',
            borderRadius: '0.75rem',
            borderSize: '2px',
            hoverTextColor: '#fff',
            hoverBg: '#125bb5',
            hoverBorderColor: '#125bb5',
            hoverBorderSize: '2.5px',
            selectedTextColor: '#fff',
            selectedBg: '#388e3c',
            selectedBorderColor: '#388e3c',
            selectedBorderSize: '3px',
          }
        }
      }
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
    hasUnsavedChanges() {
      if (!this.originalConfig) return false;
      return JSON.stringify(this.config) !== JSON.stringify(this.originalConfig);
    },
    logoSizeSlider: {
      get() {
        // Use the average of mobile and desktop sizes for the slider
        const mobileSize = parseFloat(this.config.header_styles.logoSizeMobile) || 2.5;
        const desktopSize = parseFloat(this.config.header_styles.logoSizeDesktop) || 5;
        return (mobileSize + desktopSize) / 2;
      },
      set(value) {
        // Set both mobile and desktop sizes based on the slider value
        // Mobile is 60% of the slider value, desktop is 140% of the slider value
        const mobileSize = Math.max(1, value * 0.6);
        const desktopSize = Math.max(2, value * 1.4);
        this.config.header_styles.logoSizeMobile = `${mobileSize.toFixed(1)}rem`;
        this.config.header_styles.logoSizeDesktop = `${desktopSize.toFixed(1)}rem`;
      }
    },
    brandNameSizeSlider: {
      get() {
        // Convert "1.375rem" to 1.375, handle various input formats
        const value = this.config.header_styles.brandNameSize;
        if (!value) return 1.375;
        
        // Remove 'rem' and parse the number
        const numericValue = parseFloat(value.replace('rem', ''));
        return isNaN(numericValue) ? 1.375 : numericValue;
      },
      set(value) {
        // Convert 1.375 to "1.375rem"
        this.config.header_styles.brandNameSize = `${value}rem`;
      }
    },
    searchBorderRadiusSlider: {
      get() {
        // Convert "1.5rem" to 1.5
        const value = this.config.header_styles.searchBorderRadius;
        return parseFloat(value) || 1.5;
      },
      set(value) {
        // Convert 1.5 to "1.5rem"
        this.config.header_styles.searchBorderRadius = `${value}rem`;
      }
    },
    searchBorderSizeSlider: {
      get() {
        // Convert "2px" to 2
        const value = this.config.header_styles.searchBorderSize;
        return parseFloat(value) || 2;
      },
      set(value) {
        // Convert 2 to "2px"
        this.config.header_styles.searchBorderSize = `${value}px`;
      }
    },
    sidepanelBorderRadiusSlider: {
      get() {
        // Convert "1.25rem" to 1.25
        const value = this.config.sidepanel_styles.selectedRadius;
        return parseFloat(value) || 1.25;
      },
      set(value) {
        // Convert 1.25 to "1.25rem" and apply to all border radius values
        const radiusValue = `${value}rem`;
        this.config.sidepanel_styles.selectedRadius = radiusValue;
        this.config.sidepanel_styles.hoverRadius = radiusValue;
        this.config.sidepanel_styles.unselectedRadius = radiusValue;
      }
    },
    sidepanelBorderSizeSlider: {
      get() {
        // Convert "3px" to 3
        const value = this.config.sidepanel_styles.selectedBorderSize;
        return parseFloat(value) || 3;
      },
      set(value) {
        // Convert 3 to "3px" and apply to all border size values
        const sizeValue = `${value}px`;
        this.config.sidepanel_styles.selectedBorderSize = sizeValue;
        this.config.sidepanel_styles.hoverBorderSize = sizeValue;
        this.config.sidepanel_styles.unselectedBorderSize = sizeValue;
      }
    },
    buttonBorderRadiusSlider: {
      get() {
        // Convert "0.75rem" to 0.75
        const value = this.config.details_styles.button.borderRadius;
        return parseFloat(value.replace('rem', '')) || 0.75;
      },
      set(value) {
        // Convert 0.75 to "0.75rem" and apply to all button border radius values
        const radiusValue = `${value}rem`;
        this.config.details_styles.button.borderRadius = radiusValue;
        this.config.details_styles.button.hoverBorderRadius = radiusValue;
        this.config.details_styles.button.selectedBorderRadius = radiusValue;
      }
    },
    buttonBorderSizeSlider: {
      get() {
        // Convert "2px" to 2
        const value = this.config.details_styles.button.borderSize;
        return parseFloat(value) || 2;
      },
      set(value) {
        // Convert 2 to "2px" and apply to all button border size values
        const sizeValue = `${value}px`;
        this.config.details_styles.button.borderSize = sizeValue;
        this.config.details_styles.button.hoverBorderSize = sizeValue;
        this.config.details_styles.button.selectedBorderSize = sizeValue;
      }
    }
  },
  watch: {
    'config.primaryColor': {
      handler(newColor) {
        // Reapply tab style when primary color changes
        if (this.selectedTabStyle && this.selectedTabStyle !== 'custom') {
          this.applyTabStyle();
        }
        // Reapply sidepanel style when primary color changes
        if (this.selectedSidepanelStyle && this.selectedSidepanelStyle !== 'custom') {
          this.applySidepanelStyle();
        }
      }
    },
    'config.secondaryColor': {
      handler(newColor) {
        // Reapply tab style when secondary color changes
        if (this.selectedTabStyle && this.selectedTabStyle !== 'custom') {
          this.applyTabStyle();
        }
        // Reapply sidepanel style when secondary color changes
        if (this.selectedSidepanelStyle && this.selectedSidepanelStyle !== 'custom') {
          this.applySidepanelStyle();
        }
      }
    },
    showIconsToggle: {
      handler(newValue) {
        this.config.sidepanel_styles.showIcons = newValue ? 'all' : 'none';
      }
    }
  },
  methods: {
    addTab() {
      this.config.headerTabs.push({ label: 'New Tab', value: 'new_tab' });
    },
    removeTab(index) {
      this.config.headerTabs.splice(index, 1);
    },
    handleSaveClick() {
      this.showSaveDialog = true;
    },
    async confirmSave() {
      this.showSaveDialog = false;
      await this.saveConfiguration();
    },
    async saveConfiguration() {
      try {
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        await updateDoc(workspaceRef, {
          store_locator_styles: this.config
        });
        
        // Update original config after successful save
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
        
        this.$snackbar.show('Configuration saved successfully!', 'success');
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.$snackbar.show('Error saving configuration', 'error');
      }
    },
    handleBackClick() {
      if (this.hasUnsavedChanges) {
        this.showBackDialog = true;
      } else {
        this.goBack();
      }
    },
    confirmBack() {
      this.showBackDialog = false;
      this.goBack();
    },
    goBack() {
      this.$router.go(-1);
    },
    revertAllChanges() {
      if (this.originalConfig) {
        this.config = JSON.parse(JSON.stringify(this.originalConfig));
        this.$snackbar.show('All changes reverted!', 'success');
      } else {
        this.$snackbar.show('No changes to revert', 'info');
      }
    },
    async loadConfiguration() {
      try {
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists() && workspaceDoc.data().store_locator_styles) {
          this.config = { ...this.config, ...workspaceDoc.data().store_locator_styles };
        }
        
        // Detect current tab style
        this.detectCurrentTabStyle();
        
        // Detect current sidepanel style
        this.detectCurrentSidepanelStyle();
        
        // Set showIconsToggle based on current config
        this.showIconsToggle = this.config.sidepanel_styles.showIcons === 'all';
        
        // Store original config for comparison
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
      } catch (error) {
        console.error('Error loading configuration:', error);
      }
    },
    detectCurrentTabStyle() {
      const headerStyles = this.config.header_styles || {};
      
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      const tabStyles = {
        default: {
          tabTextColor: '#222',
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: primaryColor
        },
        modern: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3F4F6',
          tabHoverBg: '#E5E7EB',
          tabSelectedBg: '#6B7280'
        },
        elegant: {
          tabTextColor: '#4C1D95',
          tabHoverTextColor: '#7C3AED',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3E8FF',
          tabHoverBg: '#E9D5FF',
          tabSelectedBg: '#7C3AED'
        },
        warm: {
          tabTextColor: '#92400E',
          tabHoverTextColor: '#EA580C',
          tabSelectedTextColor: '#fff',
          tabBg: '#FEF3C7',
          tabHoverBg: '#FED7AA',
          tabSelectedBg: '#EA580C'
        },
        nature: {
          tabTextColor: '#166534',
          tabHoverTextColor: '#16A34A',
          tabSelectedTextColor: '#fff',
          tabBg: '#DCFCE7',
          tabHoverBg: '#BBF7D0',
          tabSelectedBg: '#16A34A'
        },
        minimal: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#1F2937',
          tabBg: '#fff',
          tabHoverBg: '#F9FAFB',
          tabSelectedBg: '#F3F4F6'
        },
        dark: {
          tabTextColor: '#D1D5DB',
          tabHoverTextColor: '#F9FAFB',
          tabSelectedTextColor: '#fff',
          tabBg: '#374151',
          tabHoverBg: '#4B5563',
          tabSelectedBg: '#1F2937'
        },
        'primary-focus': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-focus': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-gradient': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`
        },
        'secondary-gradient': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`
        },
        'primary-outline': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-outline': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-soft': {
          tabTextColor: primaryColor,
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F0F7FF',
          tabHoverBg: '#DBEAFE',
          tabSelectedBg: primaryColor
        },
        'secondary-soft': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: secondaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: '#F3F4F6',
          tabSelectedBg: secondaryColor
        },
        'primary-bold': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: getDarkerShade(primaryColor),
          tabSelectedBg: getDarkerShade(primaryColor)
        },
        'secondary-bold': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: getDarkerShade(secondaryColor),
          tabSelectedBg: getDarkerShade(secondaryColor)
        }
      };

      // Check if current tab styles match any predefined style
      for (const [styleName, style] of Object.entries(tabStyles)) {
        if (
          headerStyles.tabTextColor === style.tabTextColor &&
          headerStyles.tabHoverTextColor === style.tabHoverTextColor &&
          headerStyles.tabSelectedTextColor === style.tabSelectedTextColor &&
          headerStyles.tabBg === style.tabBg &&
          headerStyles.tabHoverBg === style.tabHoverBg &&
          headerStyles.tabSelectedBg === style.tabSelectedBg
        ) {
          this.selectedTabStyle = styleName;
          return;
        }
      }
      
      // If no match found, set to custom
      this.selectedTabStyle = 'custom';
    },
    resetToDefaults() {
      this.selectedTabStyle = 'default';
      this.selectedSidepanelStyle = 'primary-soft';
      this.showIconsToggle = true;
      this.config = {
        primaryColor: '#1773EA',
        secondaryColor: '#6B7280',
        header_styles: {
          headerBg: '#f8fafc',
          tabTextColor: '#222',
          tabHoverTextColor: '#1773EA',
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: '#1773EA',
          searchBorderColor: '#1773EA',
          searchBorderRadius: '1.5rem',
          searchBorderSize: '2px',
          logoSizeMobile: '2.5rem',
          logoSizeDesktop: '5rem',
          brandNameSize: '1.375rem',
          brandNameColor: '#000000',
        },
        sidepanel_styles: {
          selectedBg: '#e6f7ff',
          selectedText: '#1773EA',
          selectedBorder: '#1773EA',
          selectedBorderSize: '3px',
          selectedRadius: '1.25rem',
          hoverBg: '#f0f7ff',
          hoverText: '#1773EA',
          hoverBorder: '#1773EA',
          hoverBorderSize: '2px',
          hoverRadius: '1.25rem',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px',
          unselectedRadius: '0.75rem',
          iconColor: '#1773EA',
          chevronColor: 'rgba(23, 115, 234, 0.7)',
          showIcons: 'all',
        },
        headerTabs: [
          { label: 'Overview', value: 'overview' },
          { label: 'Map', value: 'map' },
          { label: 'Reviews', value: 'reviews' }
        ],
        details_styles: {
          common: {
            bg: '#fff',
            textColor: '#222',
            borderColor: '#1976d2',
            borderSize: '2px',
            borderRadius: '1.25rem',
            primary: '#1976d2',
            secondary: '#424242',
            accent: '#82B1FF',
            info: '#2196F3',
            success: '#4CAF50',
            warning: '#FFC107',
            error: '#FF5252',
          },
          button: {
            textColor: '#fff',
            bg: '#1976d2',
            borderColor: '#1976d2',
            borderRadius: '0.75rem',
            borderSize: '2px',
            hoverTextColor: '#fff',
            hoverBg: '#125bb5',
            hoverBorderColor: '#125bb5',
            hoverBorderSize: '2.5px',
            selectedTextColor: '#fff',
            selectedBg: '#388e3c',
            selectedBorderColor: '#388e3c',
            selectedBorderSize: '3px',
          }
        }
      };
    },
    applyTabStyle() {
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      const tabStyles = {
        default: {
          tabTextColor: '#222',
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#e5e7eb',
          tabHoverBg: '#dbeafe',
          tabSelectedBg: primaryColor
        },
        modern: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3F4F6',
          tabHoverBg: '#E5E7EB',
          tabSelectedBg: '#6B7280'
        },
        elegant: {
          tabTextColor: '#4C1D95',
          tabHoverTextColor: '#7C3AED',
          tabSelectedTextColor: '#fff',
          tabBg: '#F3E8FF',
          tabHoverBg: '#E9D5FF',
          tabSelectedBg: '#7C3AED'
        },
        warm: {
          tabTextColor: '#92400E',
          tabHoverTextColor: '#EA580C',
          tabSelectedTextColor: '#fff',
          tabBg: '#FEF3C7',
          tabHoverBg: '#FED7AA',
          tabSelectedBg: '#EA580C'
        },
        nature: {
          tabTextColor: '#166534',
          tabHoverTextColor: '#16A34A',
          tabSelectedTextColor: '#fff',
          tabBg: '#DCFCE7',
          tabHoverBg: '#BBF7D0',
          tabSelectedBg: '#16A34A'
        },
        minimal: {
          tabTextColor: '#374151',
          tabHoverTextColor: '#1F2937',
          tabSelectedTextColor: '#1F2937',
          tabBg: '#fff',
          tabHoverBg: '#F9FAFB',
          tabSelectedBg: '#F3F4F6'
        },
        dark: {
          tabTextColor: '#D1D5DB',
          tabHoverTextColor: '#F9FAFB',
          tabSelectedTextColor: '#fff',
          tabBg: '#374151',
          tabHoverBg: '#4B5563',
          tabSelectedBg: '#1F2937'
        },
        'primary-focus': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-focus': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-gradient': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${primaryColor} 0%, ${getDarkerShade(primaryColor)} 100%)`
        },
        'secondary-gradient': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`,
          tabSelectedBg: `linear-gradient(135deg, ${secondaryColor} 0%, ${getDarkerShade(secondaryColor)} 100%)`
        },
        'primary-outline': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: primaryColor,
          tabSelectedBg: primaryColor
        },
        'secondary-outline': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: 'transparent',
          tabHoverBg: secondaryColor,
          tabSelectedBg: secondaryColor
        },
        'primary-soft': {
          tabTextColor: primaryColor,
          tabHoverTextColor: primaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F0F7FF',
          tabHoverBg: '#DBEAFE',
          tabSelectedBg: primaryColor
        },
        'secondary-soft': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: secondaryColor,
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: '#F3F4F6',
          tabSelectedBg: secondaryColor
        },
        'primary-bold': {
          tabTextColor: primaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#EFF6FF',
          tabHoverBg: getDarkerShade(primaryColor),
          tabSelectedBg: getDarkerShade(primaryColor)
        },
        'secondary-bold': {
          tabTextColor: secondaryColor,
          tabHoverTextColor: '#fff',
          tabSelectedTextColor: '#fff',
          tabBg: '#F9FAFB',
          tabHoverBg: getDarkerShade(secondaryColor),
          tabSelectedBg: getDarkerShade(secondaryColor)
        }
      };

      if (this.selectedTabStyle !== 'custom' && tabStyles[this.selectedTabStyle]) {
        const style = tabStyles[this.selectedTabStyle];
        this.config.header_styles.tabTextColor = style.tabTextColor;
        this.config.header_styles.tabHoverTextColor = style.tabHoverTextColor;
        this.config.header_styles.tabSelectedTextColor = style.tabSelectedTextColor;
        this.config.header_styles.tabBg = style.tabBg;
        this.config.header_styles.tabHoverBg = style.tabHoverBg;
        this.config.header_styles.tabSelectedBg = style.tabSelectedBg;
      }
    },
    applySidepanelStyle() {
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      // Helper function to get lighter shade of a color
      const getLighterShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = (num >> 8 & 0x00FF) + amt;
          const B = (num & 0x0000FF) + amt;
          return '#' + (0x1000000 + (R > 255 ? 255 : R) * 0x10000 +
            (G > 255 ? 255 : G) * 0x100 +
            (B > 255 ? 255 : B)).toString(16).slice(1);
        }
        return color;
      };

      const sidepanelStyles = {
        'primary-soft': {
          selectedBg: getLighterShade(primaryColor, 30),
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 20),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-bold': {
          selectedBg: primaryColor,
          selectedText: '#fff',
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(primaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(primaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-outline': {
          selectedBg: 'transparent',
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 40),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-soft': {
          selectedBg: getLighterShade(secondaryColor, 30),
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 20),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-bold': {
          selectedBg: secondaryColor,
          selectedText: '#fff',
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(secondaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(secondaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-outline': {
          selectedBg: 'transparent',
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 40),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'minimal': {
          selectedBg: '#f8fafc',
          selectedText: '#1f2937',
          selectedBorder: '#d1d5db',
          selectedBorderSize: '2px',
          hoverBg: '#f1f5f9',
          hoverText: '#1f2937',
          hoverBorder: '#cbd5e1',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#6b7280',
          unselectedBorder: '#e5e7eb',
          unselectedBorderSize: '1px'
        },
        'elegant': {
          selectedBg: '#faf5ff',
          selectedText: '#7c3aed',
          selectedBorder: '#7c3aed',
          selectedBorderSize: '3px',
          hoverBg: '#f3e8ff',
          hoverText: '#7c3aed',
          hoverBorder: '#7c3aed',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#374151',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        }
      };

      if (this.selectedSidepanelStyle !== 'custom' && sidepanelStyles[this.selectedSidepanelStyle]) {
        const style = sidepanelStyles[this.selectedSidepanelStyle];
        this.config.sidepanel_styles.selectedBg = style.selectedBg;
        this.config.sidepanel_styles.selectedText = style.selectedText;
        this.config.sidepanel_styles.selectedBorder = style.selectedBorder;
        this.config.sidepanel_styles.selectedBorderSize = style.selectedBorderSize;
        this.config.sidepanel_styles.hoverBg = style.hoverBg;
        this.config.sidepanel_styles.hoverText = style.hoverText;
        this.config.sidepanel_styles.hoverBorder = style.hoverBorder;
        this.config.sidepanel_styles.hoverBorderSize = style.hoverBorderSize;
        this.config.sidepanel_styles.unselectedBg = style.unselectedBg;
        this.config.sidepanel_styles.unselectedText = style.unselectedText;
        this.config.sidepanel_styles.unselectedBorder = style.unselectedBorder;
        this.config.sidepanel_styles.unselectedBorderSize = style.unselectedBorderSize;
      }
    },
    detectCurrentSidepanelStyle() {
      const sidepanelStyles = this.config.sidepanel_styles || {};
      
      // Get the current primary and secondary colors from config
      const primaryColor = this.config.primaryColor || '#1773EA';
      const secondaryColor = this.config.secondaryColor || '#6B7280';
      
      // Helper function to get darker shade of a color
      const getDarkerShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        }
        return color;
      };

      // Helper function to get lighter shade of a color
      const getLighterShade = (color, percent = 20) => {
        if (color.startsWith('#')) {
          const num = parseInt(color.replace('#', ''), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = (num >> 8 & 0x00FF) + amt;
          const B = (num & 0x0000FF) + amt;
          return '#' + (0x1000000 + (R > 255 ? 255 : R) * 0x10000 +
            (G > 255 ? 255 : G) * 0x100 +
            (B > 255 ? 255 : B)).toString(16).slice(1);
        }
        return color;
      };

      const stylePresets = {
        'primary-soft': {
          selectedBg: getLighterShade(primaryColor, 30),
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 20),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-bold': {
          selectedBg: primaryColor,
          selectedText: '#fff',
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(primaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(primaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'primary-outline': {
          selectedBg: 'transparent',
          selectedText: primaryColor,
          selectedBorder: primaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(primaryColor, 40),
          hoverText: primaryColor,
          hoverBorder: primaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-soft': {
          selectedBg: getLighterShade(secondaryColor, 30),
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 20),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-bold': {
          selectedBg: secondaryColor,
          selectedText: '#fff',
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getDarkerShade(secondaryColor, 10),
          hoverText: '#fff',
          hoverBorder: getDarkerShade(secondaryColor, 10),
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'secondary-outline': {
          selectedBg: 'transparent',
          selectedText: secondaryColor,
          selectedBorder: secondaryColor,
          selectedBorderSize: '3px',
          hoverBg: getLighterShade(secondaryColor, 40),
          hoverText: secondaryColor,
          hoverBorder: secondaryColor,
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#222',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        },
        'minimal': {
          selectedBg: '#f8fafc',
          selectedText: '#1f2937',
          selectedBorder: '#d1d5db',
          selectedBorderSize: '2px',
          hoverBg: '#f1f5f9',
          hoverText: '#1f2937',
          hoverBorder: '#cbd5e1',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#6b7280',
          unselectedBorder: '#e5e7eb',
          unselectedBorderSize: '1px'
        },
        'elegant': {
          selectedBg: '#faf5ff',
          selectedText: '#7c3aed',
          selectedBorder: '#7c3aed',
          selectedBorderSize: '3px',
          hoverBg: '#f3e8ff',
          hoverText: '#7c3aed',
          hoverBorder: '#7c3aed',
          hoverBorderSize: '2px',
          unselectedBg: '#fff',
          unselectedText: '#374151',
          unselectedBorder: '#e0e7ef',
          unselectedBorderSize: '1px'
        }
      };

      // Check if current sidepanel styles match any predefined style
      for (const [styleName, style] of Object.entries(stylePresets)) {
        if (
          sidepanelStyles.selectedBg === style.selectedBg &&
          sidepanelStyles.selectedText === style.selectedText &&
          sidepanelStyles.selectedBorder === style.selectedBorder &&
          sidepanelStyles.selectedBorderSize === style.selectedBorderSize &&
          sidepanelStyles.hoverBg === style.hoverBg &&
          sidepanelStyles.hoverText === style.hoverText &&
          sidepanelStyles.hoverBorder === style.hoverBorder &&
          sidepanelStyles.hoverBorderSize === style.hoverBorderSize &&
          sidepanelStyles.unselectedBg === style.unselectedBg &&
          sidepanelStyles.unselectedText === style.unselectedText &&
          sidepanelStyles.unselectedBorder === style.unselectedBorder &&
          sidepanelStyles.unselectedBorderSize === style.unselectedBorderSize
        ) {
          this.selectedSidepanelStyle = styleName;
          return;
        }
      }
      
      // If no match found, set to custom
      this.selectedSidepanelStyle = 'custom';
    }
  },
  mounted() {
    this.loadConfiguration();
  }
};
</script>

<style scoped>
/* Slider styling */
.slider {
  -webkit-appearance: none;
  appearance: none;
  background: #e5e7eb;
  outline: none;
  border-radius: 0.5rem;
  transition: background 0.2s;
}

.slider:hover {
  background: #d1d5db;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1773EA;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  background: #125bb5;
  transform: scale(1.1);
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1773EA;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  background: #125bb5;
  transform: scale(1.1);
}

/* Add any additional styles here */

/* Toggle switch styles */
input[type="checkbox"]:checked + .relative .block {
  background-color: #1773EA;
}

input[type="checkbox"]:checked + .relative .dot {
  transform: translateX(100%);
}

.relative .block {
  transition: background-color 0.3s ease;
}

.relative .dot {
  transition: transform 0.3s ease;
}

/* Enhanced dropdown styling */
select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

select:focus {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%231773ea' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
}

/* Style preset dropdown specific styling */
select[class*="border-2"] {
  position: relative;
  background-color: #ffffff;
  border: 2px solid #d1d5db;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

select[class*="border-2"]:hover {
  border-color: #9ca3af;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

select[class*="border-2"]:focus {
  border-color: #1773ea;
  box-shadow: 0 0 0 3px rgba(23, 115, 234, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style> 